import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useVideoPlatformStore } from "../../store/videoPlatformStore";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChatIcon from "@mui/icons-material/Chat";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { VideoPlatformMeeting } from "../../api/video_platform";
import { FilterableDataGrid, FilterConfig } from "../common/FilterableDataGrid";
import { RowActionsMenu, RowAction } from "../common/RowActionsMenu";
import { MeetingAttendeesDialog } from "./MeetingAttendeesDialog";
import { MeetingChatDialog } from "./MeetingChatDialog";
import { JoinMeetingDialog } from "./JoinMeetingDialog";
import {
  opensInGoogleMeet,
  resolveFallbackSubject,
  shouldPromptForHostIntent,
} from "./joinTarget";
import { useJoinMeeting } from "./useJoinMeeting";
import { getStatusColor, formatDateTime, prettifyRoomKey } from "./utils";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useAuthenticatedUserState } from "../../providers/AuthenticatedUserProvider";

interface VideoPlatformListProps {
  onView: (meetingId: string) => void;
  onDelete: (meetingId: string) => void;
}

export const VideoPlatformList: React.FC<VideoPlatformListProps> = ({
  onView,
  onDelete,
}) => {
  const { meetings, loadMeetings } = useVideoPlatformStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedRooms, setExpandedRooms] = React.useState<Set<string>>(
    new Set()
  );

  const toggleRoomExpanded = (roomId: string) => {
    setExpandedRooms((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) {
        next.delete(roomId);
      } else {
        next.add(roomId);
      }
      return next;
    });
  };

  // Continuous room grouping: segments collapse under their room row and only
  // appear (sorted by start, right below it) when the room is expanded.
  // Orphan segments (room outside the loaded page) list as plain rows.
  const rows = React.useMemo(() => {
    const segmentsByRoom = new Map<string, VideoPlatformMeeting[]>();
    const roomIds = new Set<string>();
    meetings.forEach((m) => {
      if (m.isContinuousRoom) roomIds.add(m.id);
      if (m.continuousRoomId) {
        const list = segmentsByRoom.get(m.continuousRoomId) ?? [];
        list.push(m);
        segmentsByRoom.set(m.continuousRoomId, list);
      }
    });

    const result: VideoPlatformMeeting[] = [];
    meetings.forEach((m) => {
      if (m.continuousRoomId && roomIds.has(m.continuousRoomId)) return;
      result.push(m);
      if (m.isContinuousRoom && expandedRooms.has(m.id)) {
        const children = (segmentsByRoom.get(m.id) ?? [])
          .slice()
          .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""));
        result.push(...children);
      }
    });
    return result;
  }, [meetings, expandedRooms]);

  const segmentCount = (room: VideoPlatformMeeting) =>
    room.continuousSchedule?.length ??
    meetings.filter((m) => m.continuousRoomId === room.id).length;
  const [attendeesDialogOpen, setAttendeesDialogOpen] = React.useState(false);
  const [selectedMeetingForAttendees, setSelectedMeetingForAttendees] =
    React.useState<{
      id: string;
      title: string;
    } | null>(null);
  const [chatDialogOpen, setChatDialogOpen] = React.useState(false);
  const [selectedMeetingForChat, setSelectedMeetingForChat] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const { showSnackbar, SnackbarNode } = useSnackbar();
  // Hosts only. Being an Admin is reach, not the right to run a session — the
  // same split apps-video draws between isStaff and hasHostPrivileges.
  const { isHost } = useAuthenticatedUserState();
  const handleJoinError = React.useCallback(
    (message: string) => showSnackbar(message, "error"),
    [showSnackbar]
  );
  const { join, joiningMeetingId } = useJoinMeeting({
    onError: handleJoinError,
  });
  const [joinPrompt, setJoinPrompt] = React.useState<{
    id: string;
    title: string;
  } | null>(null);

  const fallbackSubject = (meeting: VideoPlatformMeeting) =>
    resolveFallbackSubject(meeting, meetings);

  const handleJoin = (meeting: VideoPlatformMeeting) => {
    // Answered from the rows already loaded, so offering the choice — or
    // knowing not to — costs no round trip.
    if (
      shouldPromptForHostIntent({ isHost, meeting: fallbackSubject(meeting) })
    ) {
      setJoinPrompt({ id: meeting.id, title: meeting.title });
      return;
    }
    void join(meeting.id, { asHost: false });
  };

  const handleViewAttendees = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (!meeting) return;
    // Attendees are a room-level concept (one Chime session per day); segments
    // don't expose this action, so meeting here is never a segment.
    setSelectedMeetingForAttendees({
      id: meeting.externalMeetingId || meeting.id,
      title: meeting.title,
    });
    setAttendeesDialogOpen(true);
  };

  const handleCloseAttendeesDialog = () => {
    setAttendeesDialogOpen(false);
    setSelectedMeetingForAttendees(null);
  };

  const handleViewChat = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setSelectedMeetingForChat({
        id: meeting.externalMeetingId || meeting.id,
        title: meeting.title,
      });
      setChatDialogOpen(true);
    }
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedMeetingForChat(null);
  };

  // Status is filtered server-side so Ended/Cancelled meetings (windowed out of
  // the default upcoming view) are actually fetched. Type stays client-side.
  const handleFilterChange = (field: string, value: string) => {
    if (field === "status") {
      loadMeetings(value);
    }
  };

  const handleCopyMeeting = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      navigator.clipboard.writeText(JSON.stringify(meeting, null, 2));
      showSnackbar("Meeting JSON copied to clipboard!");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: isMobile ? 140 : 200,
      renderCell: (params) => {
        const meeting = params.row as VideoPlatformMeeting;
        if (meeting.isContinuousRoom) {
          const expanded = expandedRooms.has(meeting.id);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title={expanded ? "Hide sessions" : "Show sessions"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRoomExpanded(meeting.id);
                  }}
                >
                  {expanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {meeting.continuousRoomKey
                ? prettifyRoomKey(meeting.continuousRoomKey)
                : meeting.title}
              <Chip
                size="small"
                label={`${segmentCount(meeting)} sessions`}
                variant="outlined"
              />
            </Box>
          );
        }
        if (
          meeting.continuousRoomId &&
          expandedRooms.has(meeting.continuousRoomId)
        ) {
          return <Box sx={{ pl: 4.5 }}>↳ {meeting.title}</Box>;
        }
        return meeting.title;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const colors = getStatusColor(params.value as string);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                backgroundColor: colors.bgcolor,
                color: colors.color,
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "0.8125rem",
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      renderCell: (params) => params.value || "",
    },
    ...(isMobile
      ? []
      : [
          {
            field: "host",
            headerName: "Host",
            width: 150,
            sortable: false,
            renderCell: (params) => {
              const meeting = params.row as VideoPlatformMeeting;
              // Rooms rotate hosts by the hour — point at the breakdown
              // instead of pinning one name on the whole day.
              if (meeting.isContinuousRoom) {
                const hosts = new Set(
                  (meeting.continuousSchedule ?? [])
                    .map((s) => s.host?.displayName ?? s.hostId)
                    .filter(Boolean)
                );
                if (hosts.size > 1) return `${hosts.size} hosts`;
              }
              return meeting.host?.displayName ?? "";
            },
          } as GridColDef,
        ]),
    {
      field: "startDate",
      headerName: "Scheduled Start",
      width: isMobile ? 150 : 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    ...(isMobile
      ? []
      : [
          {
            field: "endDate",
            headerName: "Scheduled End",
            width: 180,
            renderCell: (params) => formatDateTime(params.value),
          } as GridColDef,
        ]),
    {
      field: "actions",
      headerName: "Actions",
      width: isMobile ? 80 : 250,
      sortable: false,
      renderCell: (params) => {
        const meeting = params.row as VideoPlatformMeeting;
        const isEnded = meeting.status?.toUpperCase() === "ENDED";
        const isRoom = !!meeting.isContinuousRoom;
        // Segments are schedule entries, not live sessions: attendees, chat and
        // the Chime session all belong to the room (one session per day), so a
        // segment exposes none of those — there's no per-slot session to look
        // at. Joining is the exception, and is handled at the Join action.
        const isSegment = !!meeting.continuousRoomId;
        // `status` here is already time-derived: the video-platform API runs
        // computeEffectiveStatus, so a Scheduled session past its
        // scheduledEndTime reads Ended even though the stored row still says
        // Created. Testing it therefore hides the action once the window has
        // passed — which matches the server, since resolveMeetingJoinUrl
        // derives the same way and answers 409 on Ended. Offering a button that
        // can only fail would be worse than not offering it.
        const isJoinable = !["ENDED", "CANCELLED"].includes(
          meeting.status?.toUpperCase() ?? ""
        );
        const actions: RowAction[] = [
          // Offered on segments as well: the resolver swaps a segment for its
          // day room, which is the real join target. Unlike attendees and chat,
          // there is nothing misleading about it.
          ...(isJoinable
            ? [
                {
                  key: "join",
                  label: opensInGoogleMeet(fallbackSubject(meeting))
                    ? "Join (opens Google Meet)"
                    : "Join Session",
                  icon: <VideoCallIcon fontSize="small" />,
                  color: "success" as const,
                  disabled: joiningMeetingId === meeting.id,
                  onClick: () => handleJoin(meeting),
                },
              ]
            : []),
          {
            key: "view",
            label: "View Details",
            icon: <VisibilityIcon fontSize="small" />,
            color: "primary",
            onClick: () => onView(meeting.id),
          },
          ...(!isSegment
            ? [
                {
                  key: "attendees",
                  label: isRoom
                    ? "View Attendees (whole day)"
                    : "View Attendees",
                  icon: <PeopleIcon fontSize="small" />,
                  color: "info" as const,
                  onClick: () => handleViewAttendees(meeting.id),
                },
                {
                  key: "chat",
                  label: isRoom ? "View Chat (whole day)" : "View Chat",
                  icon: <ChatIcon fontSize="small" />,
                  color: "secondary" as const,
                  onClick: () => handleViewChat(meeting.id),
                },
              ]
            : []),
          {
            key: "copy",
            label: "Copy JSON",
            icon: <ContentCopyIcon fontSize="small" />,
            color: "default",
            onClick: () => handleCopyMeeting(meeting.id),
          },
          ...(!isEnded && !isSegment
            ? [
                {
                  key: "end",
                  label: "End Meeting",
                  icon: <StopCircleIcon fontSize="small" />,
                  color: "error" as const,
                  onClick: () => onDelete(meeting.id),
                },
              ]
            : []),
        ];

        if (isMobile) {
          return <RowActionsMenu actions={actions} />;
        }

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {actions.map((action) => (
              // The span is load-bearing: a disabled button fires no events, so
              // MUI warns and drops the tooltip when one is the direct child.
              <Tooltip key={action.key} title={action.label}>
                <span>
                  <IconButton
                    size="small"
                    onClick={action.onClick}
                    color={action.color}
                    disabled={action.disabled}
                  >
                    {action.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
  ];

  // Category options come from the loaded meetings (distinct, non-empty).
  // Values are upper-cased to match FilterableDataGrid's case-insensitive compare.
  const categoryOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    meetings.forEach((m) => {
      const category = m.category?.trim();
      if (category) seen.set(category.toUpperCase(), category);
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [meetings]);

  const filters: FilterConfig[] = [
    {
      label: "Status",
      field: "status",
      options: [
        { value: "ALL", label: "All Statuses" },
        { value: "ACTIVE", label: "Active" },
        { value: "ENDED", label: "Ended" },
        { value: "CREATED", label: "Created" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
    },
    {
      label: "Category",
      field: "category",
      options: [{ value: "ALL", label: "All Categories" }, ...categoryOptions],
    },
  ];

  return (
    <>
      <FilterableDataGrid
        rows={rows}
        columns={columns}
        filters={filters}
        searchFields={["title", "id", "externalMeetingId"]}
        searchPlaceholder="Search by title or ID"
        height={600}
        pageSize={10}
        onFilterChange={handleFilterChange}
      />

      <MeetingAttendeesDialog
        open={attendeesDialogOpen}
        meetingId={selectedMeetingForAttendees?.id || null}
        meetingTitle={selectedMeetingForAttendees?.title || ""}
        onClose={handleCloseAttendeesDialog}
      />

      <MeetingChatDialog
        open={chatDialogOpen}
        meetingId={selectedMeetingForChat?.id || null}
        meetingTitle={selectedMeetingForChat?.title || ""}
        onClose={handleCloseChatDialog}
      />

      <JoinMeetingDialog
        open={!!joinPrompt}
        meetingTitle={joinPrompt?.title ?? ""}
        isResolving={!!joinPrompt && joiningMeetingId === joinPrompt.id}
        onClose={() => setJoinPrompt(null)}
        onJoin={({ asHost }) => {
          const target = joinPrompt;
          if (!target) return;
          // Nothing may be awaited before `join` runs: it claims the new tab
          // synchronously, and only this click still counts as a user gesture.
          void join(target.id, { asHost }).finally(() => setJoinPrompt(null));
        }}
      />

      {SnackbarNode}
    </>
  );
};
